// src/pages/admin/AdminSubscriptions.jsx
import { useContext, useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import apiService from '../../../services/apiService';
import { toast } from 'react-toastify';
import Table from '../../../components/Table';
import {
  FaEye,
  FaFileInvoice,
  FaFileCsv,
  FaTrash,
  FaSearch,
  FaTimes,
} from 'react-icons/fa';

const AdminSubscriptions = () => {
  const { user } = useContext(AuthContext);
  const [subscriptions, setSubscriptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // -----------------------------------------------------------------
  // 1. FETCH
  // -----------------------------------------------------------------
  useEffect(() => {
    if (user?.role !== 'admin') {
      toast.error('Access denied. Admins only.');
      return;
    }
    fetchSubscriptions();
  }, [user]);

  const fetchSubscriptions = async () => {
    setIsLoading(true);
    try {
      const { data } = await apiService.get('/subscriptions');
      setSubscriptions(data);
    } catch (err) {
      setError('Failed to fetch subscriptions.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // -----------------------------------------------------------------
  // 2. SEARCH + FILTER (memoized)
  // -----------------------------------------------------------------
  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return subscriptions;

    const term = searchTerm.toLowerCase();
    return subscriptions.filter(
      (sub) =>
        sub.userName?.toLowerCase().includes(term) ||
        sub.email?.toLowerCase().includes(term)
    );
  }, [subscriptions, searchTerm]);

  // Reset page when search changes
  useEffect(() => setCurrentPage(1), [searchTerm]);

  // -----------------------------------------------------------------
  // 3. PAGINATION
  // -----------------------------------------------------------------
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const paginated = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // -----------------------------------------------------------------
  // 4. ACTIONS
  // -----------------------------------------------------------------
  const handleCancel = async (userId) => {
    if (!window.confirm('Cancel this subscription?')) return;
    try {
      await apiService.delete(`/subscriptions/${userId}`);
      toast.success('Cancelled');
      fetchSubscriptions();
    } catch {
      toast.error('Failed to cancel');
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const { data } = await apiService.get(`/subscriptions/invoice/${invoiceId}`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${invoiceId}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Invoice downloaded');
    } catch {
      toast.error('Download failed');
    }
  };

  const handleExportCsv = async () => {
    try {
      const { data } = await apiService.get('/subscriptions/export/csv', {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'subscriptions.csv';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '—';

  // -----------------------------------------------------------------
  // 5. TABLE COLUMNS
  // -----------------------------------------------------------------
  const columns = [
    { key: 'userName', label: 'Name' },
    { key: 'email', label: 'Email' },
    // {
    //   key: 'subscription.subscriptionId',
    //   label: 'Sub ID',
    //   render: (r) => r.subscription.subscriptionId?.slice(-8) || '—',
    // },
    {
      key: 'subscription.status',
      label: 'Status',
      render: (r) => (
        <span
          className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
            r.subscription.status === 'active'
              ? 'bg-green-100 text-green-800'
              : 'bg-gray-100 text-gray-700'
          }`}
        >
          {r.subscription.status || '—'}
        </span>
      ),
    },
    { key: 'subscription.plan', label: 'Plan', render: (r) => r.subscription.plan || '—' },
    {
      key: 'subscription.amount',
      label: 'Price',
      render: (r) =>
        r.subscription.amount ? `$${Number(r.subscription.amount).toFixed(2)}` : '—',
    },
    {
      key: 'subscription.startDate',
      label: 'Start',
      render: (r) => formatDate(r.subscription.startDate),
    },
    {
      key: 'subscription.endDate',
      label: 'End',
      render: (r) => formatDate(r.subscription.endDate),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="flex items-center gap-3">
          <Link
            to={`/user-subscriptions/${r.userId}`}
            className="text-indigo-600 hover:text-indigo-800"
            title="View full history"
          >
            <FaEye />
          </Link>

          {r.subscription.latestInvoice && (
            <button
              onClick={() => handleDownloadInvoice(r.subscription.latestInvoice.id)}
              className="text-green-600 hover:text-green-800"
              title="Invoice"
            >
              <FaFileInvoice />
            </button>
          )}

          {r.subscription.subscriptionId && (
            <button
              onClick={() => handleCancel(r.userId)}
              className="text-red-600 hover:text-red-800"
              title="Cancel"
            >
              <FaTrash />
            </button>
          )}
        </div>
      ),
    },
  ];

  // -----------------------------------------------------------------
  // 6. RENDER
  // -----------------------------------------------------------------
  if (user?.role !== 'admin') return null;

  if (isLoading)
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="flex items-center gap-3 rounded-lg bg-white p-6 shadow">
          <svg className="h-8 w-8 animate-spin text-indigo-600" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v8h8a8 8 0 01-8 8 8 8 0 01-8-8z"
            />
          </svg>
          <span className="font-medium text-gray-700">Loading subscriptions…</span>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl">
          <p className="mb-4 text-center text-red-600">{error}</p>
          <button
            onClick={fetchSubscriptions}
            className="w-full rounded-lg bg-indigo-600 py-2.5 font-semibold text-white hover:bg-indigo-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 py-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-10 text-center">
          <h1 className="text-4xl font-extrabold text-gray-900">Manage Subscriptions</h1>
          <p className="mt-3 text-lg text-gray-600">
            Search, view, cancel, or export any subscription in seconds.
          </p>
        </div>

        {/* Toolbar */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Search */}
          <div className="relative max-w-md flex-1">
            <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or email…"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-10 pr-10 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            )}
          </div>

          {/* Export */}
          <button
            onClick={handleExportCsv}
            className="flex items-center gap-2 rounded-lg bg-green-600 px-5 py-2.5 font-semibold text-white hover:bg-green-700"
          >
            <FaFileCsv />
            Export CSV
          </button>
        </div>

        {/* Results */}
        <div className="rounded-2xl bg-white p-6 shadow-xl ring-1 ring-gray-200">
          {searchTerm && (
            <p className="mb-4 text-sm text-gray-600">
              Found <strong>{totalItems}</strong> subscription{totalItems !== 1 ? 's' : ''}{' '}
              {searchTerm && `matching “${searchTerm}”`}
            </p>
          )}

          {totalItems === 0 ? (
            <div className="py-12 text-center">
              <p className="text-lg text-gray-500">
                {searchTerm ? 'No subscriptions match your search.' : 'No subscriptions yet.'}
              </p>
            </div>
          ) : (
            <>
              <Table columns={columns} data={paginated} />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-6 flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
                  <p className="text-sm text-gray-600">
                    Showing {paginated.length} of {totalItems} entries
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                    >
                      Previous
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                      <button
                        key={n}
                        onClick={() => setCurrentPage(n)}
                        className={`rounded border px-3 py-1 text-sm ${
                          currentPage === n
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {n}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSubscriptions;